/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import Toolbar from '@material-ui/core/Toolbar';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { isFeatureEnabled, ToggleableFeature } from '../../shared/utils/featuresUtils';
import { RoutePath } from '../../shared/enums/RoutePath';
import TopNavigation from '../menu/TopNavigation';
import ListItemSidebar from './ListItemSidebar';
import { SidebarContext } from './useSidebar';

const useStyles = makeStyles(theme => ({
  list: ({ showConfigurableHeader }) => ({
    marginTop: showConfigurableHeader ? '1rem' : '2rem',
    [theme.breakpoints.down('sm')]: {
      marginTop: '0',
    },
  }),
  drawerOpen: {
    [theme.breakpoints.up('md')]: {
      width: '18.5rem',
    },
    width: '100%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(13),
    fontSize: '1.7rem',
    [theme.breakpoints.up('md')]: {
      width: theme.spacing(13),
    },
  },
  drawer: ({ scrollUp }) => ({
    backgroundColor: theme.palette.sidebarBackground,
    paddingTop: (scrollUp || scrollUp === 0) && `${165 - scrollUp}px`,
    border: 'none',
    color: theme.palette.sidebarText,
    marginTop: '-1rem',
    height: 'calc(100vh + 2rem)',
    [theme.breakpoints.down('md')]: {
      width: '20rem',
    },
  }),
  divider: {
    borderTop: '1px solid white',
  },
  topNavigation: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginLeft: '1rem',
    height: '100%',
  },
}));

export default function Sidebar({
  sidebarList,
  open,
  globalConfiguration,
  user,
  currentOrganization,
  showConfigurableHeader,
}) {
  const { t } = useTranslation();
  const isIndividualOrganization = currentOrganization?.type === 'INDIVIDUAL';

  const [scrollUp, setScrollUp] = useState(showConfigurableHeader ? 0 : null);

  const shouldScrollUp = () => {
    if (window.scrollY >= 0 && window.scrollY <= 90) {
      setScrollUp(window.scrollY);
    }
  };
  useEffect(() => {
    if (showConfigurableHeader) {
      window.addEventListener('scroll', shouldScrollUp);
    }
    return () => {
      if (showConfigurableHeader) {
        window.removeEventListener('scroll', shouldScrollUp);
      }
    };
  }, []);

  const { isExpandedDrawer, isMediumOrBigger, onMouseLeave } = useContext(SidebarContext);
  const classes = useStyles({ isExpandedDrawer, scrollUp });

  return (
    <div className={classes.root}>
      <Drawer
        variant={isMediumOrBigger ? 'permanent' : 'persistent'}
        classes={{
          paper: classnames(
            classes.drawer,
            isMediumOrBigger && {
              [classes.drawerOpen]: isExpandedDrawer,
              [classes.drawerClose]: !isExpandedDrawer,
            },
          ),
        }}
        BackdropProps={{ invisible: true }}
        anchor="left"
        open={open}
        onMouseLeave={onMouseLeave}
      >
        {!showConfigurableHeader && <Toolbar />}
        <nav aria-label={t('aria.header.sidebar.nav')}>
          <List className={classes.list}>
            {sidebarList?.map((sidebarItem, indexID) => (
              <li key={sidebarItem.text}>
                <ListItemSidebar indexID={indexID} sidebarItem={sidebarItem} />
              </li>
            ))}

            {isFeatureEnabled(globalConfiguration?.resource, ToggleableFeature.USER_MANAGEMENT) &&
              user?.currentOrganizationId &&
              !isIndividualOrganization && (
              <li>
                <ListItemSidebar
                  indexID="user-management"
                  sidebarItem={{
                    text: t('user-management'),
                    link: RoutePath.generateRoutePath(RoutePath.USER_SEARCH, {
                      organization: user.currentOrganizationId,
                    }),
                    icon: 'manage_accounts',
                  }}
                  key="user-management"
                />
              </li>
            )}
          </List>
        </nav>
        {!isMediumOrBigger && (
          <>
            <Divider className={classes.divider} />
            <div className={classes.topNavigation}>
              <TopNavigation user={user} currentOrganization={currentOrganization} />
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
